import { z } from 'zod';

/**
 * Whether a conversation still accepts messages.
 *
 * Three states. Two of them mirror `MatchStatusSchema` deliberately: a
 * conversation exists because a match does (N13), and it stops accepting writes
 * when that match stops. Keeping the vocabulary parallel means nobody has to
 * hold a translation table in their head, and the day a third match state
 * appears the question "what does chat do about it" is asked in one place.
 *
 * - `active` — the match is standing and both members may write.
 * - `closed` — it is read-only. The history stays readable: a member who was
 *   blocked or unmatched still has a record of what was said to them, and
 *   deleting it would be a moderation decision this module has no business
 *   taking on its own.
 * - `pending_guardian_approval` — the match is standing, but a ward's guardian
 *   has not opened it yet (P24b2, `guardian.matchApprovalRequired`). Also
 *   read-only, and **not the same fact as `closed`**: this one is waiting on
 *   somebody and will very likely open, which is why it is a third value rather
 *   than a flag beside `closed`.
 *
 * ## The third state is not "not writable"
 *
 * `closed` was the only non-writable state for six phases, so every consumer
 * spelled "has this ended?" as `!isWritable…` — and on the mobile client that
 * literally rendered the sentence "This conversation has ended". A predicate
 * that answers the *waiting* question by name is therefore exported beside the
 * writability one: the two are different questions, and a caller that needs the
 * difference must not have to reconstruct it from a negation.
 */
export const CONVERSATION_STATUSES = ['active', 'closed', 'pending_guardian_approval'] as const;

export const ConversationStatusSchema = z.enum(CONVERSATION_STATUSES);

export type ConversationStatus = (typeof CONVERSATION_STATUSES)[number];

/**
 * A conversation is born from a live match, so there is no draft state.
 *
 * Still `active`: the gate decides per match whether to depart from it, and it
 * departs by passing a status rather than by changing this default — a
 * conversation with no guardian in the picture is unaffected by P24b2.
 */
export const INITIAL_CONVERSATION_STATUS: ConversationStatus = 'active';

/**
 * May this conversation be written to?
 *
 * A predicate rather than an `=== 'active'` at each call site, for
 * `isActiveMatchStatus`'s reason: the write guard, the read path and P19's
 * listener must never disagree about what "open" means.
 */
export function isWritableConversationStatus(status: ConversationStatus): boolean {
  return status === 'active';
}

/**
 * Is this conversation waiting for a guardian to open it?
 *
 * Read-only like `closed`, and **for the opposite reason** — see the note above.
 * Every surface that explains why a member cannot type needs to tell the two
 * apart.
 */
export function isAwaitingGuardianApprovalStatus(status: ConversationStatus): boolean {
  return status === 'pending_guardian_approval';
}
